import { useLang } from "@/context/LangContext";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Privacy() {
  const { lang, isRTL } = useLang();
  const navigate = useNavigate();
  const Chev = isRTL ? ChevronRight : ChevronLeft;

  return (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 bg-white border-b border-[#EAEAEA] z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full hover:bg-[#F5F5F5] flex items-center justify-center">
            <Chev className="w-5 h-5" />
          </button>
          <h1 className="font-bold text-lg">{lang === "ar" ? "سياسة الخصوصية" : "Privacy Policy"}</h1>
        </div>
      </div>
      <article className="max-w-3xl mx-auto px-5 py-6 prose prose-sm text-[#1A1A1A] leading-relaxed">
        {lang === "ar" ? (
          <div className="space-y-4 text-[14px]">
            <p className="text-xs text-[#6B6B6B]">آخر تحديث: فبراير 2026</p>

            <Section title="١. البيانات التي نجمعها">
              <p><b>معلومات الحساب:</b> الاسم، البريد الإلكتروني، رقم الجوال، كلمة المرور (مشفّرة).</p>
              <p><b>بيانات الموقع:</b> مع إذنك، نجمع الإحداثيات الجغرافية (GPS) لعرض المطاعم القريبة وإيصال الطلبات. يُستخدم الموقع فقط أثناء استخدام التطبيق.</p>
              <p><b>بيانات الطلبات:</b> المطاعم التي طلبت منها، الأصناف، العناوين المحفوظة.</p>
              <p><b>بيانات الدفع:</b> لا نخزّن أرقام البطاقات. جميع المدفوعات تتم عبر بوابة دفع آمنة.</p>
            </Section>

            <Section title="٢. كيف نستخدم البيانات">
              <ul className="list-disc ps-5 space-y-1">
                <li>إتمام طلباتك وإيصالها</li>
                <li>عرض المطاعم القريبة من موقعك</li>
                <li>التواصل معك بخصوص طلباتك</li>
                <li>تحسين جودة الخدمة</li>
              </ul>
            </Section>

            <Section title="٣. مشاركة البيانات">
              <p>نشارك بياناتك فقط مع:</p>
              <ul className="list-disc ps-5 space-y-1">
                <li><b>المطاعم:</b> اسمك، رقم هاتفك، عنوان التوصيل</li>
                <li><b>المناديب:</b> اسمك، رقم هاتفك، عنوان التوصيل</li>
                <li><b>بوابة الدفع:</b> بيانات الدفع فقط</li>
              </ul>
              <p className="mt-2">لا نبيع بياناتك لأي طرف ثالث لأغراض تسويقية.</p>
            </Section>

            <Section title="٤. حقوقك">
              <ul className="list-disc ps-5 space-y-1">
                <li>حذف حسابك: اطلبه عبر <a className="text-[#E05D3A]" href="mailto:support@jayek.sa">support@jayek.sa</a></li>
                <li>تعديل بياناتك من صفحة الملف الشخصي</li>
                <li>إيقاف الموقع من إعدادات جوالك متى شئت</li>
              </ul>
            </Section>

            <Section title="٥. الأمان">
              <p>جميع البيانات مشفّرة أثناء النقل عبر HTTPS. كلمات المرور مخزّنة مشفّرة بـBcrypt.</p>
            </Section>

            <Section title="٦. التواصل">
              <p>📧 <a className="text-[#E05D3A]" href="mailto:support@jayek.sa">support@jayek.sa</a></p>
            </Section>
          </div>
        ) : (
          <div className="space-y-4 text-[14px]">
            <p className="text-xs text-[#6B6B6B]">Last updated: February 2026</p>

            <Section title="1. Data We Collect">
              <p><b>Account:</b> Name, email, phone, password (encrypted).</p>
              <p><b>Location:</b> With your consent, GPS coordinates are collected to show nearby restaurants and deliver orders. Used only while the app is in use.</p>
              <p><b>Orders:</b> Restaurants ordered from, items, saved addresses.</p>
              <p><b>Payment:</b> We do not store card numbers. All payments go through a secure gateway.</p>
            </Section>

            <Section title="2. How We Use Data">
              <ul className="list-disc ps-5 space-y-1">
                <li>Process and deliver your orders</li>
                <li>Show nearby restaurants</li>
                <li>Contact you about your orders</li>
                <li>Improve service quality</li>
              </ul>
            </Section>

            <Section title="3. Data Sharing">
              <p>We share data only with:</p>
              <ul className="list-disc ps-5 space-y-1">
                <li><b>Restaurants:</b> name, phone, delivery address</li>
                <li><b>Drivers:</b> name, phone, delivery address</li>
                <li><b>Payment gateway:</b> payment details only</li>
              </ul>
              <p className="mt-2">We do NOT sell your data to third parties for marketing.</p>
            </Section>

            <Section title="4. Your Rights">
              <ul className="list-disc ps-5 space-y-1">
                <li>Delete your account: request via <a className="text-[#E05D3A]" href="mailto:support@jayek.sa">support@jayek.sa</a></li>
                <li>Edit your data from the profile page</li>
                <li>Disable location from your phone's settings anytime</li>
              </ul>
            </Section>

            <Section title="5. Security">
              <p>All data is encrypted in transit over HTTPS. Passwords are stored hashed with Bcrypt.</p>
            </Section>

            <Section title="6. Contact">
              <p>📧 <a className="text-[#E05D3A]" href="mailto:support@jayek.sa">support@jayek.sa</a></p>
            </Section>
          </div>
        )}
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-bold text-[15px] text-[#E05D3A] mb-1.5">{title}</h2>
      <div className="text-[#1A1A1A] leading-relaxed space-y-1.5">{children}</div>
    </section>
  );
}
